import io from 'socket.io-client'
import { computed, ref } from 'vue'
import { get, refAutoReset, set, useTimestamp } from '@vueuse/core'
import { time2Date } from '@/utils/tools'
import { add, div, mul, percent } from '@/utils/math'
import { getDomain } from '@/utils'

export const socket = io(`wss://ws-swap.${getDomain()}`, {
  query: {
    version: '1.0.0',
    exchangeClient: 'PC',
  },
  transports: ['websocket'],
})

export const accountsName = {
  1: 'future',
  10: 'bonus',
  11: 'follow',
  30: 'grants',
}
export const accountsType = {
  future: 1,
  bonus: 10,
  follow: 11,
  grants: 30,
}
export const timestamp = useTimestamp()
export const accounts = ref({})
export const depthChartData = ref({})
export const match = ref({})
export const orderbook = ref({})
export const trades = ref([])
export const positions = ref([])
export const positionLoading = ref(true)
export const openOrders = ref(new Map())
export const positionConfig = ref({ loading: true })
export const positionsExtra = ref({})
export const homePageTopTen24 = ref([])

export const kline = {
  spot: ref({}),
  futures: ref({}),
}
export const overview = {
  spot: ref({}),
  futures: ref({}),
}

export const wallet = ref({ bonus: {}, follow: {}, future: {}, loading: true })

export const resetTrades = () => set(trades, [])
export const resetOrderbook = () => set(orderbook, {})
export function resetData() { // for o
  set(openOrders, new Map())
  set(wallet, { bonus: {}, follow: {}, future: {}, loading: true })
  set(positions, [])
  set(positionConfig, { loading: true })
  set(positionsExtra, {})
  set(accounts, {})
}

export function getOverviewByCode(code, cid, type = 'futures') {
  return get(overview[type])[code] || get(overview[type])[cid] || {}
}
const bridges = new Set()
export const toC = computed(() => (from, to, amount) => { // 币转币
  if (from === to)
    return amount

  const ov = get(overview.spot)
  const o = ov[from + to]
  if (o && +o.c)
    return mul(amount, o.c)

  const revO = ov[to + from]
  if (revO && +revO.c)
    return div(amount, revO.c)

  for (const bridge of bridges) {
    const o1 = ov[from + bridge]
    const o2 = ov[bridge + from]
    const o3 = ov[bridge + to]
    const o4 = ov[to + bridge]
    if (o1 && +o1.c) {
      if (o3 && +o3.c)
        return mul(amount, o1.c).mul(o3.c)

      if (o4 && +o4.c)
        return mul(amount, o1.c).div(o4.c)
    }
    if (o2 && +o2.c) {
      if (o3 && +o3.c)
        return div(amount, o2.c).mul(o3.c)

      if (o4 && +o4.c)
        return div(amount, o2.c).div(o4.c)
    }
  }
  for (const symbol in ov) {
    if (symbol.startsWith(from) || symbol.endsWith(from)) {
      const bridge = symbol.replace(from, '')
      if (!bridges.has(bridge)) {
        bridges.add(symbol.replace(from, ''))
        return get(toC)(from, to, amount)
      }
    }
  }
})
export const toDemo = computed(() => (from, to, amount) => { // 币转币，只支持to为quote的情况
  if (from === to)
    return amount

  const o = get(overview.spot)[from + to]
  if (o)
    return Number(div(amount, o.c))
})

export const toBTC = computed(() => (code, amount = 0) => get(toC)(code, 'BTC', amount))
export const toUSDT = computed(() => (code, amount) => get(toC)(code, 'USDT', amount))
export function toE({ quote, code, type }) { // 交易对转USDT
  const o = get(overview[type])
  const price = o[code]?.c
  if (code?.startsWith('USDT'))
    return 1

  if (price) {
    if (quote === 'USDT')
      return price

    const xUSDT = o[`${quote}USDT`]
    if (xUSDT)
      return mul(price, xUSDT.c)

    const USDTx = o[`USDT${quote}`]
    if (USDTx?.c)
      return div(price, USDTx?.c)
  }
}

const formatter = {
  home_page_top_ten24: (data) => {
    homePageTopTen24.value = data?.ids || []
  },
  snapshot_depth: (data) => {
    set(orderbook, { ...data, code: data.sc, id: data.contractId })
  },
  indicator: (data, { type }) => {
    if (data.indicators?.length) {
      for (const o of data.indicators) {
        if (!get(overview[type])[o.cid])
          get(overview[type])[o.cid] = {}

        for (const k in o)
          get(overview[type])[o.cid][k] = o[k]

        get(overview[type])[o.cid].mark = o.mp
        get(overview[type])[o.cid].index = o.ip
      }
    }
    if (data.markets?.length) {
      overview[type].value = data.markets.reduce((sofar, cur) => {
        if (cur.lp && cur.lp !== '0') {
          cur.c = cur.lp
          cur.r = cur.pcr
          cur.a = cur.pv
          cur.v = cur.pt
          if (cur.mp) {
            cur.mark = cur.mp
            cur.index = cur.ip
          }
          if ('pcr' in cur)
            cur.rt = percent(cur.r)

          if (!sofar[cur.cid])
            sofar[cur.cid] = {}

          for (const k in cur)
            sofar[cur.cid][k] = cur[k]
        }
        return sofar
      }, overview[type].value ?? {})
    }
    if (data.accounts?.length) {
      const w = get(wallet)
      accounts.value = data.accounts.reduce((sofar, cur) => {
        sofar[cur.aid] = sofar[accountsName[cur.at]] = cur
        sofar.names ??= {}
        sofar.names[cur.aid] = accountsName[cur.at]
        if (cur.at === 1)
          sofar.mainId = cur.aid

        for (const o of cur.assets) {
          const x = {
            real: o.avail,
            trade: Math.max(o.avail, 0),
            upnl: o.upnl,
          }
          const name = accountsName[cur.at]
          const typed = w[name]
          if (typed) {
            const cy = typed[o.cid]
            if (cy)
              Object.assign(cy, x)
          }
          else {
            w[name] = {
              [o.cid]: x,
            }
          }
        }
        const pse = {}
        for (const o of cur.posis) {
          const { pid } = o
          pse[`${pid}`] = o
        }
        Object.assign(positionsExtra.value, pse)
        get(positionConfig).loading = false // TODO
        return sofar
      }, accounts.value ?? {})
      w.loading = false // TODO: 目前spot_match不推送，不确定是bug还是feature，所以只能在这处理wallet的状态
    }
  },
  future_kline: (data, { otherParams, type }) => {
    set(kline[type], data)
  },
  match: (data, { type }) => {
    const mt = data.mt || data.messageType
    const accountName = accountsName[data.accountType]
    if ([3002].includes(mt)) {
      const w = get(wallet)
      const id = data.currencyId

      w[accountName][id] = Object.assign({}, w[accountName][id], {
        ...data,
        trade: Math.max(data.available, 0),
        frozen: data.frozenInitMargin,
        real: data.available,
      })
      get(wallet).loading = false
    }
    if ([3022].includes(mt)) {
      get(positionConfig)[accountName] ??= {}
      Object.assign(get(positionConfig)[accountName], data.positionConfgs?.reduce((sofar, cur) => {
        const auto = false // 不支持自动杠杆
        // const auto = cur.leverage === 0
        sofar[cur.contractId] = {
          auto,
          id: cur.contractId,
          isolated: cur.isolated,
          leverage: cur.leverage,
        }
        return sofar
      }, {}), { loading: false })
    }
    else if ([3012].includes(mt)) {
      set(positionLoading, false)
      set(positions, data.posis.sort((a, b) => b.id - a.id))
    }
    else if ([3004, 3016].includes(mt)) {
      const o = get(openOrders)
      // 0-条件单,1-正在申报,2-已申报未成交,3-部分成交,4-全部成交,5-部分撤单,6-全部撤单,7-撤单中,8-失效,11-缓存高于条件的委托,12-缓存低于条件的委托
      if (['CANCELED', 'FILLED', 'STOPPED', 4, 5, 6].includes(data.orderStatus)) {
        o.delete(data.orderId)
      }

      else {
        o.set(data.orderId, {
          ...data,
          id: data.orderId,
          pid: data.positionId,
        })
      }
    }
  },
  realtime: ({ timeSlice }) => {
    set(timestamp, Math.floor(timeSlice / 1000))
  },
  future_snapshot_depth: (data) => {
    set(depthChartData, data)
  },
  future_tick: (data) => {
    const [ts, price, amount, side] = data.trades
    const { contractId } = data
    if (overview.futures.value[contractId]) {
      overview.futures.value[contractId].c = price
      set(timestamp, ts / 1000)
    }
    const arr = [{
      id: ts,
      time: time2Date(ts / 1000, 'h:i:s'),
      price,
      amount,
      side: +side,
    }].concat(trades.value).slice(0, 50)
    trades.value = arr
  },
}
function dispatcher(data, topic, type) {
  // console.log('data', data)
  data && formatter[topic]?.(data, { topic, type })
}
const alias = {
  orderbook: 'snapshot_depth',
  depthChart: 'future_snapshot_depth',
  futures: 'future',
}
async function getBody({ topic, contractId, step, ranges }) {
  topic = alias[topic] || topic

  if (formatter[topic]) {
    const params = {}
    if (contractId)
      Object.assign(params, { symbols: [{ symbol: contractId }] })

    if (step)
      Object.assign(params, { symbols: [{ ...params.symbols[0], step }] })

    if (ranges)
      Object.assign(params, { symbols: [{ ...params.symbols[0], ranges }] })

    return {
      topics: [{
        topic,
        params,
      }],
    }
  }
}

const subs = ref({})

export async function subscribe(options) {
  const body = await getBody(options)
  const subData = {
    header: {
      type: 1003,
    },
    body,
  }

  if (body) {
    const [{ topic }] = body.topics
    socket.on(topic, data => options.ignore ? null : dispatcher(data, topic, 'futures'))
    socket.emit('subscribe', subData)
    get(subs)[options.guid || options.topic] = { subData, ...options }
    return { guid: options.guid || topic, topic: options.topic }
  }
}

export function keepTopic(topic) {
  const { [topic]: o } = get(subs)
  if (o) {
    return {
      type: o.type,
      resend: () => subscribe(o),
    }
  }
  return {
    resend: () => null,
  }
}

const authed = ref(false)
const authToken = ref('')
export function auth(token) {
  if (token && !get(authed)) {
    socket.emit('auth', {
      header: {
        type: 1001,
      },
      body: {
        token,
      },
    })
    socket.once('auth', () => {
      set(authed, true)
      set(authToken, token)
    })
  }
}
export const reconnected = refAutoReset(false, 1000)
socket.once('reconnect', () => {
  socket.on('connect', () => {
    resetData()
    set(reconnected, true)
    set(authed, false)
    auth(get(authToken))
    const o = get(subs)
    for (const i in o)
      socket.emit('subscribe', o[i].subData)
  })
})

if (import.meta.client && !import.meta.env.PROD) {
  window.db = {
    accounts,
    wallet,
    positions,
    positionConfig,
    match,
    orderbook,
    trades,
    positionsExtra,
    kline,
    openOrders,
  }
}
